import React, { useState, useEffect } from 'react';
import {
  Box, TextField, Button, CircularProgress, Typography,
  List, ListItem, ListItemText, Checkbox, Dialog, DialogTitle,
  DialogContent, DialogActions, Link, Chip, InputAdornment, IconButton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import api from '../utils/api';
import { sanitizedHtml } from '../utils/sanitize';

function Search() {
  const [keyword, setKeyword] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProgramDetail, setSelectedProgramDetail] = useState(null);

  useEffect(() => {
    document.title = '節目搜尋';
  }, []);

  const handleSearch = () => {
    if (!keyword.trim()) return;

    setLoading(true);
    setHasSearched(true);

    api.get('/search', { params: { keyword } })
      .then(res => {
        setSearchResults(res.data?.programs || []);
        setLoading(false);
      })
      .catch(err => {
        console.error('搜尋失敗:', err);
        setSearchResults([]);
        setLoading(false);
      });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const clearSearch = () => {
    setKeyword('');
    setSearchResults([]);
    setHasSearched(false);
  };

  const formatProgramTime = (timeString) => {
    if (!timeString) return '時間未知';
    const year = timeString.substring(0, 4);
    const month = timeString.substring(4, 6);
    const day = timeString.substring(6, 8);
    const hour = timeString.substring(8, 10);
    const minute = timeString.substring(10, 12);
    const second = timeString.substring(12, 14);
    const d = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}+09:00`);
    return d.toLocaleString('zh-TW', { timeZone: 'Asia/Tokyo' });
  };

  const handleProgramClick = (program) => {
    setSelectedProgramDetail(program);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => setOpenDialog(false);

  const handleProgramSelect = (program) => {
    const idx = selectedPrograms.findIndex(p => p.id === program.id);
    const next = [...selectedPrograms];
    if (idx === -1) next.push(program);
    else next.splice(idx, 1);
    setSelectedPrograms(next);
  };

  const submitRecordings = async () => {
    if (selectedPrograms.length === 0) return;
    const failures = [];
    await Promise.all(selectedPrograms.map(async (program) => {
      try {
        // 後端只用 station_id 和 start_time，節目 metadata 由後端向 radiko 反查
        await api.post('/programs', {
          station_id: program.channelId,
          start_time: program.ft,
        });
      } catch (err) {
        console.error('提交錄音失敗:', err);
        failures.push(program.title || program.id);
      }
    }));

    if (failures.length === 0) {
      alert('錄製任務已成功提交');
    } else {
      alert(`部分錄製任務提交失敗：\n${failures.join('\n')}`);
    }

    setSearchResults(prev => prev.map(program => {
      if (selectedPrograms.some(s => s.id === program.id && !failures.includes(program.title || program.id))) {
        return { ...program, scheduled: true };
      }
      return program;
    }));
    setSelectedPrograms([]);
  };

  return (
    <Box>
      <Typography variant="h5" component="h1" gutterBottom>
        節目搜尋
      </Typography>

      <Box sx={{ display: 'flex', mb: 3 }}>
        <TextField
          fullWidth
          label="輸入關鍵字"
          variant="outlined"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onKeyDown={handleKeyDown}
          InputProps={{
            endAdornment: keyword && (
              <InputAdornment position="end">
                <IconButton onClick={clearSearch} edge="end">
                  <CloseIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        <IconButton
          aria-label="搜尋"
          onClick={handleSearch}
          disabled={!keyword.trim() || loading}
          sx={{
            ml: 1,
            alignSelf: 'center',
            width: 48,
            height: 48,
            bgcolor: 'primary.main',
            color: '#fff',
            '&:hover': { bgcolor: 'primary.dark' },
            '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'text.disabled' },
          }}
        >
          <SearchIcon />
        </IconButton>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {hasSearched && !loading && searchResults.length === 0 && (
        <Typography variant="body1" color="textSecondary" align="center">
          沒有找到符合「{keyword}」的節目
        </Typography>
      )}

      {searchResults.length > 0 && (
        <>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            找到 {searchResults.length} 個符合「{keyword}」的節目
          </Typography>

          {selectedPrograms.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              onClick={submitRecordings}
              sx={{ mb: 2 }}
            >
              提交 {selectedPrograms.length} 個錄製任務
            </Button>
          )}

          <List>
            {searchResults.map((program, index) => (
              <ListItem
                key={index}
                button
                onClick={() => handleProgramClick(program)}
                style={{ opacity: program.scheduled ? 0.5 : 1 }}
                divider
              >
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <ListItemText
                      primary={program.title}
                      secondary={
                        <>
                          <Typography component="span" variant="body2" color="textSecondary">
                            時間：{formatProgramTime(program.ft)} - {formatProgramTime(program.to)}
                          </Typography>
                          <br />
                          <Typography component="span" variant="body2" color="textSecondary">
                            頻道：{program.channelName}
                          </Typography>
                        </>
                      }
                    />
                    <Checkbox
                      edge="end"
                      checked={selectedPrograms.some(p => p.id === program.id)}
                      disabled={program.scheduled}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (!program.scheduled) handleProgramSelect(program);
                      }}
                    />
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>
        </>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{selectedProgramDetail?.title}</span>
          <IconButton onClick={handleCloseDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box>
            {selectedProgramDetail?.img && (
              <img
                src={selectedProgramDetail.img}
                alt="Program"
                style={{ maxWidth: '100%', marginBottom: '10px' }}
              />
            )}

            <Typography variant="body2" color="textSecondary" gutterBottom>
              頻道：{selectedProgramDetail?.channelName}
            </Typography>

            <Typography variant="body2" color="textSecondary" gutterBottom>
              時間：{formatProgramTime(selectedProgramDetail?.ft)} - {formatProgramTime(selectedProgramDetail?.to)}
            </Typography>

            <Typography
              variant="body1"
              component="div"
              dangerouslySetInnerHTML={sanitizedHtml(selectedProgramDetail?.description || '')}
              sx={{ my: 2 }}
            />

            {Array.isArray(selectedProgramDetail?.person) && selectedProgramDetail.person.length > 0 && (
              <Typography variant="body2" color="textSecondary" gutterBottom>
                演出者：{selectedProgramDetail.person.map(p => p.name).join(', ')}
              </Typography>
            )}

            <Typography variant="body2" color="textSecondary" gutterBottom>
              節目：{selectedProgramDetail?.season_name || '未知'}
            </Typography>

            <Typography variant="body2" color="textSecondary" gutterBottom>
              摘要：<span dangerouslySetInnerHTML={sanitizedHtml(selectedProgramDetail?.summary || '無摘要')} />
            </Typography>

            {selectedProgramDetail?.url && (
              <Link href={selectedProgramDetail.url} target="_blank" rel="noopener" sx={{ display: 'block', mb: 1 }}>
                更多訊息
              </Link>
            )}

            <Box sx={{ mt: 1 }}>
              {Array.isArray(selectedProgramDetail?.tag) && selectedProgramDetail.tag.map((tag, i) => (
                <Chip key={typeof tag === 'string' ? tag : i} label={typeof tag === 'string' ? tag : tag.name || JSON.stringify(tag)} sx={{ m: 0.5 }} />
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleCloseDialog}>關閉</Button>
          {!selectedProgramDetail?.scheduled && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                handleProgramSelect(selectedProgramDetail);
                handleCloseDialog();
              }}
            >
              {selectedPrograms.some(p => p.id === selectedProgramDetail?.id) ? '取消選擇' : '選擇此節目'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Search;
